/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Row, Col } from "antd"
import { AppState } from "store/store.type"
import { useTranslationsMap } from "common/utils/i18n"
import { SelectMenuDrawer } from "components/drawers"
import { CREATE_CATALOGUE, CREATE_MENU } from "constants/routes"
import { cardStyle } from "styles/common"
import { TroyWebTracker } from "utils/tracking"
import {
  EventCategory,
  EventAction,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"
import { useHistory, useParams } from "react-router"
import { MenuDisplayOption } from "common/constants/misc"
import { AnyAction } from "redux"
import { actionCreator } from "store/store"
import { AxiosAction, ResponseWithError } from "types/api"
import {
  useCreateMenuGroupMutation,
  useLazyFetchMenuGroupDetailQuery,
} from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { MerchantStoreInfo } from "types/models/menu"
import { MenuEditor } from "pages/menu/detail/MenuEditor"
import { CreateMenuGroupTrackerEntryPoint } from "constants/menu_group"
import { useIsMartMenuGroup } from "pages/menu-groups/useIsMartMenuGroup"
import { getDalErrMessage, parseJSONSafely } from "utils/misc"
import { waitMenuGroupPropagete } from "utils/menu_group"
import { MenuBookFilled, MerchantFilled } from "@duxton-web/icons"
import {
  selectMenuCard,
  selectMenuCardDescription,
  selectMenuCardIconContainer,
  selectMenuCardTitle,
  selectMenuContainer,
  selectMenuIcon,
} from "./style"

export interface SelectMenuProps {
  selectedStores: MerchantStoreInfo[]
  menuGroupName: string
  entryPoint: CreateMenuGroupTrackerEntryPoint
  onChangeEntryPoint: (entryPoint: CreateMenuGroupTrackerEntryPoint) => void
}

export const SelectMenu: FC<SelectMenuProps> = ({
  selectedStores,
  menuGroupName,
  entryPoint,
  onChangeEntryPoint,
}: SelectMenuProps) => {
  const { merchantGroupID } = useSelector((state: AppState) => state.menu)
  const { menuParentID } = useParams<{
    menuParentID: string
    tabKey: string
  }>()
  const isMartMenuGroup = useIsMartMenuGroup()

  const history = useHistory()
  const dispatch = useDispatch()
  const [showDrawerExisting, setShowDrawerExisting] = useState(false)

  const {
    flags: {
      createMenuGroup: isRtkCreateMenuGroupEnabled,
      fetchMenuGroupDetail: isRtkFetchMenuGroupDetailEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [createMenuGroupMutation] = useCreateMenuGroupMutation()
  const [fetchMenuGroupDetailRtk] = useLazyFetchMenuGroupDetailQuery()

  const textAsBizType = isMartMenuGroup ? "forCatalogue" : "forMenu"

  const textMap = useTranslationsMap({
    existingCardTitle: `menu.menuGroup.create.${textAsBizType}.useExistingMenu`,
    existingCardDescription: `menu.menuGroup.create.${textAsBizType}.pickCurrentMenu`,
    fromScratchCardTitle: `menu.menuGroup.create.${textAsBizType}.setupMenu`,
    fromScratchCardDescription: `menu.menuGroup.create.${textAsBizType}.buildMenuFromScratch`,
  })

  const createUrl = useMemo(
    () => (isMartMenuGroup ? CREATE_CATALOGUE : CREATE_MENU),
    [isMartMenuGroup],
  )

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationSelectMenu,
      action: EventAction.pageView,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createMenuGroupErrorCallback = useCallback(
    (errResp: ResponseWithError) => {
      const { message = "" } = errResp

      const dalErrMsg = getDalErrMessage(message, true)
      if (dalErrMsg) {
        return dalErrMsg
      }

      const dataAsObj = parseJSONSafely(message)
      if (dataAsObj?.merchantGroup) {
        return dataAsObj?.merchantGroup
      }

      return true
    },
    [],
  )

  const handleClickCreate = async (existingMenuGroupID?: string) => {
    const { businessType, menuDisplayOption, timezone } = selectedStores?.[0]
    if (menuGroupName && menuDisplayOption && timezone) {
      dispatch(actionCreator.global.showLoading())

      if (!isRtkFlagLoading && isRtkCreateMenuGroupEnabled) {
        const result = await createMenuGroupMutation({
          merchantGroupID,
          menuGroup: {
            name: menuGroupName,
            businessType: businessType || 0,
            menuType: menuDisplayOption === MenuDisplayOption.category ? 0 : 1,
            timeZone: timezone,
          },
          menuGroupID: existingMenuGroupID,
          linkedStoreIDs: selectedStores?.map(store => store.merchantID),
        })

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.createMenuGroup,
          labels: {
            MERCHANT_ID: merchantGroupID,
            IS_RTK_ENABLED: isRtkCreateMenuGroupEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "createMenuGroup",
          },
        })

        if (!result.error && result.data?.menuGroupID) {
          const newlyCreatedMenuGroupID = result.data.menuGroupID
          const didPropagate = await waitMenuGroupPropagete({
            menuGroupID: newlyCreatedMenuGroupID,
            merchantGroupID,
            dispatch,
            isRtkEnabled: isRtkFetchMenuGroupDetailEnabled,
            triggerFetchMenuGroupDetailRtk: fetchMenuGroupDetailRtk,
          })
          if (didPropagate) {
            onChangeEntryPoint(
              existingMenuGroupID
                ? EventAction.clickUseExistingMenu
                : EventAction.clickCreateNewMenu,
            )
            history.replace(`${createUrl}/${newlyCreatedMenuGroupID}`)
          }
        }
      } else if (!isRtkFlagLoading) {
        const resp = (await dispatch(
          actionCreator.menu.createMenuGroup({
            merchantGroupID,
            menuGroup: {
              name: menuGroupName,
              businessType: businessType || 0,
              menuType:
                menuDisplayOption === MenuDisplayOption.category ? 0 : 1,
              timeZone: timezone,
            },
            menuGroupID: existingMenuGroupID,
            linkedStoreIDs: selectedStores?.map(store => store.merchantID),
            errorCallback: createMenuGroupErrorCallback,
          }) as AnyAction,
        )) as AxiosAction<{ menuGroupID: string }>

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.createMenuGroup,
          labels: {
            MERCHANT_ID: merchantGroupID,
            IS_RTK_ENABLED: isRtkCreateMenuGroupEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "createMenuGroup",
          },
        })

        let newlyCreatedMenuGroupID: string | undefined
        if (resp?.payload?.data) {
          newlyCreatedMenuGroupID = resp?.payload?.data?.menuGroupID
        }

        if (resp?.type.endsWith("_SUCCESS") && newlyCreatedMenuGroupID) {
          const didPropagate = await waitMenuGroupPropagete({
            menuGroupID: newlyCreatedMenuGroupID,
            merchantGroupID,
            dispatch,
            isRtkEnabled: isRtkFetchMenuGroupDetailEnabled,
            triggerFetchMenuGroupDetailRtk: fetchMenuGroupDetailRtk,
          })
          if (didPropagate) {
            onChangeEntryPoint(
              existingMenuGroupID
                ? EventAction.clickUseExistingMenu
                : EventAction.clickCreateNewMenu,
            )
            history.replace(`${createUrl}/${newlyCreatedMenuGroupID}`)
          }
        }
      }

      dispatch(actionCreator.global.hideLoading())
    }
  }

  const handleClickExisting = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationSelectMenu,
      action: EventAction.clickUseExistingMenu,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
      },
    })
    setShowDrawerExisting(true)
  }

  const handleClickFromScratch = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupCreationSelectMenu,
      action: EventAction.clickCreateNewMenu,
      labels: {
        MERCHANT_GROUP_ID: merchantGroupID,
      },
    })
    handleClickCreate()
  }

  const cards = [
    {
      dataTestId: "select-menu-existing",
      icon: <MerchantFilled css={selectMenuIcon} />,
      title: textMap.existingCardTitle,
      description: textMap.existingCardDescription,
      onClick: () => handleClickExisting(),
    },
    {
      dataTestId: "select-menu-from-scratch",
      icon: <MenuBookFilled css={selectMenuIcon} />,
      title: textMap.fromScratchCardTitle,
      description: textMap.fromScratchCardDescription,
      onClick: () => handleClickFromScratch(),
    },
  ]

  return (
    <div css={selectMenuContainer}>
      {menuParentID ? (
        <div
          css={css`
            height: 100%;
            display: flex;
            flex-flow: column;
          `}
        >
          <MenuEditor
            data-testid="menu-editor-content"
            routePrefix={CREATE_MENU}
            eventLabels={{
              ENTRY_POINT: entryPoint,
            }}
          />
        </div>
      ) : (
        <Row type="flex" gutter={[32, 32]}>
          {cards.map(card => (
            <Col
              data-testid={card.dataTestId}
              key={card.title}
              xs={24}
              md={12}
              xl={8}
            >
              <div
                css={css`
                  ${cardStyle}
                  ${selectMenuCard}
                `}
                className="clickable"
                onClick={card.onClick}
              >
                <div css={selectMenuCardIconContainer}>{card.icon}</div>
                <p css={selectMenuCardTitle}>{card.title}</p>
                <p css={selectMenuCardDescription}>{card.description}</p>
              </div>
            </Col>
          ))}
        </Row>
      )}
      <SelectMenuDrawer
        visible={showDrawerExisting}
        onClose={() => setShowDrawerExisting(false)}
        onClickCreate={handleClickCreate}
      />
    </div>
  )
}
